import Modal from '@mui/material/Modal';
import { ThemeProvider } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import _ from 'lodash';

import Alerts from 'pages/Alerts/Index';
import CruiseDataUpload from 'pages/CruiseDataUpload/Index';
import EventsLog from 'pages/EventsLog/Index';
import Home from 'pages/Home/Index';
import Login from 'pages/Login/Index';
import NusSignIn from 'pages/Login/NusSignIn';
import { getAuth, hasAdminAccess, loadSession, useAuth } from 'utils/auth';
import { useSetRecoilState } from 'recoil';
import { jwtData } from 'pages/OnBoarding/state';
import OnBoarding from 'pages/OnBoarding/Index';
import Owners from 'pages/Owners/Index';
import Parameters from 'pages/Parameters/Index';
import Users from 'pages/Users/Index';
import Tokens from 'pages/ApiKeys/Index';

import { ConstantsProvider } from 'components/providers/ConstantsProvider';
import { QueryProvider } from 'components/providers/QueryProvider';
import ProtectedRoute from 'components/ProtectedRoute';
import { useSetParams } from 'pages/Qc/state';
import api, { fetchMesnParameters } from 'utils/api';

import theme from './theme/theme';

function GlobalModal() {
  return (
    <Modal open={true}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress color='primary' size={40} />
      </div>
    </Modal>
  );
}

function MyRoutes() {
  const setParams = useSetParams();
  const auth = useAuth();
  const setJwtData = useSetRecoilState(jwtData);
  useEffect(() => {
    void loadSession().catch(() => {});
  }, []);
  useEffect(() => {
    setJwtData(auth.user as any);
  }, [auth.user, setJwtData]);

  useEffect(() => {
    let active = true;
    const execute = async () => {
      if (!hasAdminAccess(auth.user)) {
        setParams({});
        return;
      }
      const params: any = await fetchMesnParameters();
      if (active) setParams(params);
    };

    execute().catch(() => {
      if (active) setParams({});
    });
    return () => {
      active = false;
    };
  }, [auth.user?.userId]);

  return (
    <Routes>
      <Route
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
        path='/'
      />
      <Route
        element={
          <ProtectedRoute>
            <OnBoarding />
          </ProtectedRoute>
        }
        path='/instrument-on-boarding'
      />
      <Route
        element={
          <ProtectedRoute>
            <EventsLog />
          </ProtectedRoute>
        }
        path='/events'
      />
      <Route
        element={
          <ProtectedRoute>
            <CruiseDataUpload />
          </ProtectedRoute>
        }
        path='/cruise-data-upload'
      />
      <Route
        element={
          <ProtectedRoute>
            <Alerts />
          </ProtectedRoute>
        }
        path='/alerts'
      />
      <Route
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
        path='/users'
      />
      <Route
        element={
          <ProtectedRoute>
            <Owners />
          </ProtectedRoute>
        }
        path='/owners'
      />
      <Route
        element={
          <ProtectedRoute>
            <Parameters />
          </ProtectedRoute>
        }
        path='/parameters'
      />
      <Route
        element={
          <ProtectedRoute>
            <Tokens />
          </ProtectedRoute>
        }
        path='/apikeys'
      />
      <Route element={<Login />} path='/login' />
      <Route element={<NusSignIn />} path='/auth/oidc' />
    </Routes>
  );
}

function App() {
  const [showModal, setShowModal] = useState(false);

  const didSetup = useRef(false);

  useEffect(() => {
    if (didSetup.current) return;
    didSetup.current = true;

    api.interceptors.request.use(
      (config) => {
        const show = getAuth().ready && !(config as any).dontShowGlobalModal;
        setShowModal(show);
        return config;
      },
      (error) => {
        setShowModal(false);
        return Promise.reject(error);
      }
    );

    api.interceptors.response.use(
      (response) => {
        setShowModal(false);
        return response;
      },
      (error) => {
        setShowModal(false);
        return Promise.reject(error);
      }
    );
  }, []);

  return (
    <QueryProvider>
      <div>
        {showModal && <GlobalModal />}
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <RecoilRoot>
              <ConstantsProvider>
                <MyRoutes />
              </ConstantsProvider>
            </RecoilRoot>
          </ThemeProvider>
        </BrowserRouter>
      </div>
    </QueryProvider>
  );
}

export default App;
